import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { Send, Check, Star, Trash2, Upload, ImageIcon, Loader2, MessageSquare } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import PreviewLightbox from "./PreviewLightbox";

function ProposalCard({ orderId, proposal, isAdmin, onReload }) {
  const [previewUrls, setPreviewUrls] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPreviews = useCallback(async () => {
    if (previewUrls) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/orders/${orderId}/proposals/${proposal.id}/preview-urls`
      );
      setPreviewUrls(data.urls || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [orderId, proposal.id, previewUrls]);

  useEffect(() => {
    loadPreviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.id]);

  const openAt = (i) => {
    setLightboxIdx(i);
    setShowLightbox(true);
  };

  const toggleDirection = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/proposals/${proposal.id}`, {
        is_selected_direction: !proposal.is_selected_direction,
      });
      toast.success(
        proposal.is_selected_direction
          ? "Direction retenue retirée"
          : "Direction retenue mise à jour"
      );
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    if (!window.confirm("Confirmer la validation de cette création ?")) return;
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/proposals/${proposal.id}/validate`);
      toast.success("Création validée. Merci !");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const unvalidate = async () => {
    if (!window.confirm("Réinitialiser la validation client ?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/proposals/${proposal.id}/unvalidate`);
      toast.success("Validation réinitialisée");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteProposal = async () => {
    if (!window.confirm(`Supprimer définitivement « ${proposal.title} » ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/proposals/${proposal.id}`);
      toast.success("Proposition supprimée");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/proposals/${proposal.id}/comments`, {
        body: comment,
      });
      setComment("");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`border-2 p-5 md:p-6 ${
        proposal.is_selected_direction
          ? "border-[#0E7C90] bg-[#0E7C90]/[.03]"
          : "border-black/10 bg-white"
      }`}
      data-testid={`proposal-card-${proposal.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="cs-eyebrow text-neutral-500">
            Proposition {String(proposal.sequence).padStart(2, "0")}
          </div>
          <h3 className="cs-h3 mt-1 text-xl md:text-2xl">{proposal.title}</h3>
          <div className="mt-1 text-[11px] font-mono text-neutral-500">
            {fmtDateTime(proposal.published_at || proposal.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {proposal.is_selected_direction && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#0E7C90] text-[#FAF8F5] text-[11px] font-mono uppercase tracking-[0.14em]" data-testid={`proposal-selected-badge-${proposal.id}`}>
              <Star size={12} /> Direction retenue
            </span>
          )}
          {proposal.client_validated_at && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white text-[11px] font-mono uppercase tracking-[0.14em]" data-testid={`proposal-validated-badge-${proposal.id}`}>
              <Check size={12} /> Validée par le client
            </span>
          )}
        </div>
      </div>

      {/* Aperçus */}
      <div className="mt-4">
        {loading && (
          <div className="text-sm text-neutral-500 font-mono flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Chargement des aperçus…
          </div>
        )}
        {!loading && previewUrls?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {previewUrls.map((p, i) => (
              <button
                type="button"
                key={p.file_id}
                onClick={() => openAt(i)}
                onContextMenu={(e) => e.preventDefault()}
                className="group relative overflow-hidden border border-black/10 bg-neutral-100 aspect-[4/3] hover:border-[#0E7C90] transition-colors"
                data-testid={`proposal-preview-${proposal.id}-${i}`}
              >
                <img
                  src={p.url}
                  alt={`Aperçu ${i + 1}`}
                  className="w-full h-full object-contain select-none pointer-events-none"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end justify-end p-2">
                  <span className="opacity-0 group-hover:opacity-100 text-[10px] font-mono uppercase tracking-[0.14em] bg-white/95 text-black px-2 py-1">
                    Agrandir · page {i + 1}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {!isAdmin && !proposal.client_validated_at && (
          <button
            type="button"
            onClick={validate}
            disabled={busy}
            className="cs-btn-primary inline-flex items-center gap-2"
            data-testid={`proposal-validate-${proposal.id}`}
          >
            <Check size={14} /> Valider cette création
          </button>
        )}
        {isAdmin && (
          <>
            <button
              type="button"
              onClick={toggleDirection}
              disabled={busy}
              className={`inline-flex items-center gap-2 px-4 py-2 border text-sm ${
                proposal.is_selected_direction
                  ? "border-[#0E7C90] text-[#0E7C90] bg-[#0E7C90]/10"
                  : "border-black/25 text-black hover:border-[#0E7C90]"
              }`}
              data-testid={`proposal-direction-${proposal.id}`}
            >
              <Star size={14} />
              {proposal.is_selected_direction
                ? "Retirer la direction"
                : "Marquer comme direction retenue"}
            </button>
            {proposal.client_validated_at && (
              <button
                type="button"
                onClick={unvalidate}
                disabled={busy}
                className="cs-btn-ghost !text-black !border-black text-sm"
                data-testid={`proposal-unvalidate-${proposal.id}`}
              >
                Réinitialiser la validation
              </button>
            )}
            <button
              type="button"
              onClick={deleteProposal}
              disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-2 border border-red-300 text-red-600 hover:bg-red-50 text-sm"
              data-testid={`proposal-delete-${proposal.id}`}
            >
              <Trash2 size={13} /> Supprimer
            </button>
          </>
        )}
      </div>

      {proposal.client_validated_at && (
        <div className="mt-3 text-[12px] font-mono text-emerald-700">
          Validée le {fmtDateTime(proposal.client_validated_at)}
          {proposal.client_validated_by?.name
            ? ` par ${proposal.client_validated_by.name}`
            : ""}
        </div>
      )}

      {/* Fil de commentaires */}
      <div className="mt-5 border-t border-black/10 pt-4">
        <div className="cs-eyebrow text-neutral-500 inline-flex items-center gap-1">
          <MessageSquare size={12} /> Discussion sur cette proposition
        </div>
        <ul className="mt-3 space-y-3">
          {(proposal.comments || []).map((c) => (
            <li
              key={c.id || c.created_at}
              className={`p-3 border ${
                c.author_role === "admin"
                  ? "border-[#0E7C90]/40 bg-[#0E7C90]/5"
                  : "border-black/10 bg-white"
              }`}
              data-testid={`proposal-comment-${c.id || ""}`}
            >
              <div className="flex justify-between text-xs font-mono text-neutral-500">
                <span>
                  {c.author_name} · {c.author_role === "admin" ? "Studio" : "Client"}
                </span>
                <span>{fmtDateTime(c.created_at)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm">{c.body}</p>
            </li>
          ))}
          {(proposal.comments || []).length === 0 && (
            <li className="text-sm text-neutral-500">
              Aucun retour pour l'instant.
            </li>
          )}
        </ul>
        <div className="mt-3 flex gap-2">
          <textarea
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="cs-input flex-1"
            placeholder="Votre retour sur cette proposition…"
            data-testid={`proposal-comment-input-${proposal.id}`}
          />
          <button
            type="button"
            onClick={postComment}
            disabled={busy || !comment.trim()}
            className="cs-btn-primary self-end"
            data-testid={`proposal-comment-send-${proposal.id}`}
          >
            <Send size={14} />
          </button>
        </div>
      </div>

      <PreviewLightbox
        open={showLightbox}
        pages={previewUrls || []}
        initialIndex={lightboxIdx}
        onClose={() => setShowLightbox(false)}
        title={proposal.title}
      />
    </div>
  );
}

function ProposalPublisher({ orderId, onReload }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) {
      toast.error("Sélectionnez un fichier (PDF ou image).");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (title.trim()) fd.append("title", title.trim());
    setBusy(true);
    try {
      await api.post(`/admin/orders/${orderId}/proposals`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Proposition publiée");
      setFile(null);
      setTitle("");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-[#0E7C90]/40 bg-[#0E7C90]/5 p-5" data-testid="proposal-publisher">
      <div className="cs-eyebrow text-[#0E7C90] inline-flex items-center gap-1">
        <Upload size={12} /> Publier une nouvelle proposition
      </div>
      <p className="mt-2 text-[13px] text-neutral-600">
        Le fichier original sera conservé en accès administrateur uniquement. Des
        aperçus rasterisés et filigranés seront générés automatiquement pour le
        client. PDF ou image (JPG, PNG, WEBP).
      </p>
      <div className="mt-3 grid gap-3">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre optionnel (ex : Piste graphique — variante bleue)"
          className="cs-input"
          data-testid="proposal-publisher-title"
        />
        <input
          type="file"
          accept=".pdf,image/png,image/jpeg,image/webp"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm"
          data-testid="proposal-publisher-file"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !file}
          className="cs-btn-primary self-start inline-flex items-center gap-2"
          data-testid="proposal-publisher-submit"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Publier
        </button>
      </div>
    </div>
  );
}

export default function ProposalsSection({ orderId, isAdmin }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}/proposals`);
      setProposals(data || []);
    } catch (e) {
      // noop — silent when order forbidden
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="cs-card p-6" data-testid="proposals-section">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="cs-eyebrow text-[#0E7C90]">Propositions</div>
        <div className="text-[12px] font-mono text-neutral-500">
          Aperçus protégés · filigranés · non téléchargeables
        </div>
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-neutral-500 font-mono">Chargement…</div>
      ) : proposals.length === 0 ? (
        <div className="mt-4 p-6 border border-dashed border-black/15 bg-neutral-50 text-center">
          <ImageIcon size={22} className="mx-auto text-neutral-400" />
          <p className="mt-2 text-sm text-neutral-600">
            Aucune proposition publiée pour le moment.
          </p>
          {!isAdmin && (
            <p className="mt-1 text-[12px] text-neutral-500">
              Les propositions apparaîtront ici dès leur mise en ligne par le studio.
            </p>
          )}
        </div>
      ) : (
        <ul className="mt-5 space-y-6">
          {proposals.map((p) => (
            <li key={p.id}>
              <ProposalCard
                orderId={orderId}
                proposal={p}
                isAdmin={isAdmin}
                onReload={load}
              />
            </li>
          ))}
        </ul>
      )}

      {isAdmin && (
        <div className="mt-6 border-t border-black/10 pt-5">
          <ProposalPublisher orderId={orderId} onReload={load} />
        </div>
      )}
    </div>
  );
}
